import { OrganizationConfigs } from "../shared/bootstrap/app-config";
import { FlightListColumn } from "./models/flight-list-column.model";
import { FlightListColumnGroup } from "./models/flight-list-column-group.model";

/** The width of the column of the detail toggle, which Kendo draws (the theme's own), and of the pin column before the others. */
export const EXPAND_COLUMN_WIDTH = 32;
export const PIN_COLUMN_WIDTH = 48;
/** Where the held columns of the flight start: after the two before them. */
const HELD_START = EXPAND_COLUMN_WIDTH + PIN_COLUMN_WIDTH;

export const PAGE_SIZES = [20, 50, 100];
export const SEARCH_DELAY_MS = 600;

export const SEARCHED_FIELDS = ["flightType", "vemd", "pilot1Name", "pilot1Role", "pilot2Name", "pilot2Role", "departurePlace", "arrivalPlace", "fuelName", "firstRefuelLocation"];

export const COLUMN_GROUPS: FlightListColumnGroup[] = [
    {
        title: "FLIGHT_LIST.groups.flight",
        items: [
            { field: "registrationMark", title: "FLIGHT_LIST.registrationMark", width: 130, type: "text", stickyLeft: HELD_START },
            { field: "flightNumber", title: "FLIGHT_LIST.flightNumber", width: 120, type: "number", filterable: true, stickyLeft: HELD_START + 130, stickyEdge: true },
            { field: "pageNumber", title: "FLIGHT_LIST.docBlock", width: 120, type: "docBlock" },
            { field: "flightType", title: "FLIGHT_LIST.flightType", width: 150, type: "text", filterable: true, sortable: true },
            { field: "isPositioning", title: "FLIGHT_LIST.isPositioning", width: 110, type: "boolean", visibleWhen: (configs) => configs.positioningFlightEnabled },
            { field: "isHotRefuelFlight", title: "FLIGHT_LIST.hotRefuel", width: 110, type: "boolean", filterable: true, visibleWhen: (configs) => configs.multiStopFlightEnabled },
            { field: "vemd", title: "VEMD", width: 100, type: "text", filterable: true, sortable: true, visibleWhen: (configs) => configs.vemdEnabled },
        ],
    },
    {
        title: "FLIGHT_LIST.groups.leg",
        items: [
            { field: "departurePlace", title: "FLIGHT_LIST.from", width: 200, type: "place", airportField: "departureId", filterable: true, sortable: true, left: true },
            { field: "arrivalPlace", title: "FLIGHT_LIST.to", width: 200, type: "place", airportField: "arrivalId", filterable: true, sortable: true, left: true },
        ],
    },
    {
        title: "FLIGHT_LIST.groups.dateTime",
        items: [
            {
                title: "FLIGHT_LIST.start",
                columns: [
                    { field: "startDate", title: "FLIGHT_LIST.columnDate", width: 110, type: "date", sortable: true },
                    { field: "startTime", title: "FLIGHT_LIST.columnTime", width: 90, type: "time", sortable: true },
                ],
            },
            {
                title: "FLIGHT_LIST.takeOff",
                columns: [
                    { field: "flightDate", title: "FLIGHT_LIST.columnDate", width: 120, type: "date", sortable: true },
                    { field: "takeOffTime", title: "FLIGHT_LIST.columnTime", width: 90, type: "time", sortable: true },
                ],
            },
            {
                title: "FLIGHT_LIST.landing",
                columns: [
                    { field: "arrivalDate", title: "FLIGHT_LIST.columnDate", width: 120, type: "date", sortable: true },
                    { field: "landingTime", title: "FLIGHT_LIST.columnTime", width: 100, type: "time", sortable: true },
                ],
            },
            {
                title: "FLIGHT_LIST.stop",
                columns: [
                    { field: "stopDate", title: "FLIGHT_LIST.columnDate", width: 110, type: "date", sortable: true },
                    { field: "stopTime", title: "FLIGHT_LIST.columnTime", width: 90, type: "time", sortable: true },
                ],
            },
            {
                title: "FLIGHT_LIST.duration",
                columns: [
                    { field: "flightTime", title: "FLIGHT_LIST.flightTime", width: 100, type: "time", sortable: true },
                    { field: "startStopTime", title: "FLIGHT_LIST.startStopTime", width: 110, type: "time", sortable: true },
                ],
            },
        ],
    },
    {
        title: "FLIGHT_LIST.groups.airframe",
        items: [
            {
                title: "FLIGHT_LIST.airframeHours",
                columns: [
                    { field: "helicopterData.previousAirFrameHours", title: "FLIGHT_LIST.previousValue", width: 110, type: "text" },
                    { field: "helicopterData.totalAirFrameHours", title: "FLIGHT_LIST.totalValue", width: 110, type: "text" },
                ],
            },
            {
                title: "FLIGHT_LIST.airframeEngineTime",
                columns: [
                    { field: "helicopterData.previousAirFrameStartStopHours", title: "FLIGHT_LIST.previousValue", width: 110, type: "text" },
                    { field: "helicopterData.totalAirFrameStartStopHours", title: "FLIGHT_LIST.totalValue", width: 110, type: "text" },
                ],
            },
            {
                title: "FLIGHT_LIST.cycles",
                columns: [
                    { field: "helicopterData.previousAirFrameCycles", title: "FLIGHT_LIST.previousValue", width: 110, type: "number" },
                    { field: "cycles", title: "FLIGHT_LIST.ofFlight", width: 110, type: "number", filterable: true, sortable: true },
                    { field: "helicopterData.totalAirFrameCycles", title: "FLIGHT_LIST.totalValue", width: 110, type: "number" },
                ],
            },
            // {
            //     title: "FLIGHT_LIST.landings",
            //     columns: [
            //         { field: "helicopterData.previousLandings", title: "FLIGHT_LIST.previousValue", width: 110, type: "number" },
            //         { field: "landings", title: "FLIGHT_LIST.ofFlight", width: 110, type: "number", filterable: true, sortable: true },
            //         { field: "helicopterData.totalLandings", title: "FLIGHT_LIST.totalValue", width: 110, type: "number" },
            //     ],
            // },
        ],
    },
    ...([1, 2] as const).map<FlightListColumnGroup>((engine) => ({
        title: `FLIGHT_LIST.groups.engine${engine}`,
        items: [
            {
                title: "FLIGHT_LIST.airframeHours",
                columns: [
                    { field: `helicopterData.previousEngine${engine}Hours`, title: "FLIGHT_LIST.previousValue", width: 110, type: "text" },
                    { field: `helicopterData.totalEngine${engine}Hours`, title: "FLIGHT_LIST.totalValue", width: 110, type: "text" },
                ],
            },
            {
                title: "FLIGHT_LIST.engineTime",
                columns: [
                    { field: `helicopterData.previousEngine${engine}StartStopHours`, title: "FLIGHT_LIST.previousValue", width: 110, type: "text" },
                    { field: `helicopterData.totalEngine${engine}StartStopHours`, title: "FLIGHT_LIST.totalValue", width: 110, type: "text" },
                ],
            },
            ...(["N1", "N2", "Imp"] as const).map((reading) => ({
                title: `FLIGHT_LIST.reading${reading}`,
                columns: [
                    { field: `engineData.engine${engine}Previous${reading}`, title: "FLIGHT_LIST.previousValue", width: 100, type: "decimal" as const },
                    { field: `engineData.engine${engine}Actual${reading}`, title: "FLIGHT_LIST.ofFlight", width: 90, type: "decimal" as const },
                    { field: `engineData.engine${engine}Total${reading}`, title: "FLIGHT_LIST.totalValue", width: 100, type: "decimal" as const },
                ],
            })),
        ],
    })),
    {
        title: "FLIGHT_LIST.groups.crew",
        items: [
            {
                title: "FLIGHT_LIST.pilot",
                columns: [
                    { field: "pilot1Name", title: "FLIGHT_LIST.name", width: 170, type: "text", filterable: true, sortable: true, left: true },
                    { field: "pilot1Role", title: "FLIGHT_LIST.role", width: 90, type: "pilotRole", sortable: true },
                ],
            },
            {
                title: "FLIGHT_LIST.copilot",
                columns: [
                    { field: "pilot2Name", title: "FLIGHT_LIST.name", width: 170, type: "text", filterable: true, sortable: true, left: true },
                    { field: "pilot2Role", title: "FLIGHT_LIST.role", width: 90, type: "pilotRole", sortable: true },
                ],
            },
            {
                field: "passengers",
                title: "FLIGHT_LIST.passengers",
                width: 110,
                type: "number",
                filterable: true,
                sortable: true,
                visibleWhen: (configs) => configs.passengerEnabled,
                titleWhen: (configs) => (configs.pobEnabled ? "FLIGHT_LIST.edit.headcount.pob" : "FLIGHT_LIST.passengers"),
            },
            {
                field: "payingPassengers",
                title: "FLIGHT_LIST.payingPassengers",
                width: 110,
                type: "number",
                filterable: true,
                sortable: true,
                visibleWhen: (configs) => configs.payingPassengersEnabled,
            },
            { field: "isTax", title: "FLIGHT_LIST.isTax", width: 110, type: "boolean", filterable: true, sortable: true, visibleWhen: (configs) => configs.taxEnabled },
        ],
    },
    {
        title: "FLIGHT_LIST.groups.fuel",
        items: [
            { field: "fuelName", title: "FLIGHT_LIST.fuelName", width: 130, type: "text", filterable: true, sortable: true },
            { field: "fuelPlanned", title: "FLIGHT_LIST.fuelPlanned", width: 100, type: "text" },
            { field: "fuelArrival", title: "FLIGHT_LIST.fuelArrival", width: 100, type: "text" },
            { field: "refuel", title: "FLIGHT_LIST.refuel", width: 100, type: "text" },
            { field: "totalFuel", title: "FLIGHT_LIST.totalFuel", width: 100, type: "text" },
            { field: "firstRefuelLocation", title: "FLIGHT_LIST.refuelLocation", width: 200, type: "text", filterable: true, sortable: true },
        ],
    },
    {
        title: "FLIGHT_LIST.groups.hook",
        items: [
            { field: "hooks.cycles", title: "FLIGHT_LIST.hook.cycles", width: 90, type: "hook", hookField: "cycles" },
            { field: "hooks.hours", title: "FLIGHT_LIST.hook.hours", width: 90, type: "hook", hookField: "hours" },
            { field: "hooks.totalCycles", title: "FLIGHT_LIST.hook.totalCycles", width: 110, type: "hook", hookField: "totalCycles" },
            { field: "hooks.totalHours", title: "FLIGHT_LIST.hook.totalHours", width: 110, type: "hook", hookField: "totalHours" },
        ],
    },
    {
        title: "FLIGHT_LIST.groups.hoist",
        items: [
            { field: "hoists.cycles", title: "FLIGHT_LIST.hoist.cycles", width: 90, type: "hoist", hoistField: "cycles" },
            { field: "hoists.hours", title: "FLIGHT_LIST.hoist.hours", width: 90, type: "hoist", hoistField: "hours" },
            { field: "hoists.totalCycles", title: "FLIGHT_LIST.hoist.totalCycles", width: 110, type: "hoist", hoistField: "totalCycles" },
            { field: "hoists.totalHours", title: "FLIGHT_LIST.hoist.totalHours", width: 110, type: "hoist", hoistField: "totalHours" },
        ],
    },
    {
        title: "FLIGHT_LIST.groups.timestamps",
        items: [
            {
                title: "FLIGHT_LIST.created",
                columns: [
                    { field: "createdAt", title: "FLIGHT_LIST.onServer", width: 150, type: "dateTime", sortable: true },
                    { field: "clientCreatedAt", title: "FLIGHT_LIST.onDevice", width: 150, type: "dateTime", sortable: true },
                    { field: "createdOffline", title: "FLIGHT_LIST.offline", width: 100, type: "boolean", sortable: true, filterable: true },
                ],
            },
            {
                title: "FLIGHT_LIST.picAcceptance",
                columns: [
                    { field: "pilotConfirmedAt", title: "FLIGHT_LIST.acceptedAt", width: 150, type: "dateTime", sortable: true },
                    { field: "confirmedOffline", title: "FLIGHT_LIST.offline", width: 100, type: "boolean", sortable: true, filterable: true },
                ],
            },
        ],
    },
];

/** The columns of a group, those under a header of their own included. */
export const columnsOf = (group: FlightListColumnGroup): FlightListColumn[] => group.items.flatMap((item) => ("columns" in item ? item.columns : [item]));

/** What decides which columns the grid shows: the aircraft of the page and the organization's configurations. */
export interface GridContext {
    /** Whether an aircraft of the page has a second engine. */
    twinEngine: boolean;
    configs: OrganizationConfigs;
}

/**
 * The groups as this page shows them: without the columns of the second engine when no aircraft has one, and without
 * the columns the organization's configurations turn off (`visibleWhen`), the headers they change retitled (`titleWhen`).
 * A subgroup or group left without columns goes too.
 */
export const visibleGroups = (groups: FlightListColumnGroup[], context: GridContext): FlightListColumnGroup[] => {
    const shown = (column: FlightListColumn) => (context.twinEngine || !/engine2/i.test(column.field)) && (column.visibleWhen?.(context.configs) ?? true);
    const retitled = (column: FlightListColumn): FlightListColumn => (column.titleWhen ? { ...column, title: column.titleWhen(context.configs) } : column);
    return groups
        .map((group) => ({
            ...group,
            items: group.items
                .map((item) => ("columns" in item ? { ...item, columns: item.columns.filter(shown).map(retitled) } : shown(item) ? retitled(item) : null))
                .filter((item) => item !== null && (!("columns" in item) || item.columns.length > 0)),
        }))
        .filter((group) => group.items.length > 0);
};
